import {
  Input,
  Output,
  OnChanges,
  OnDestroy,
  Component,
  EventEmitter,
  SimpleChanges,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

import { tap, filter, Subscription, Subject, take } from 'rxjs';

import { ObjectAttributes } from '@shared/floor-plan/models';
import { TableSizes } from '@shared/floor-plans';
import { Shapes } from '@shared/core';
import {
  ConfirmationModalComponent,
  ConfirmationModalResult,
  ValidationService,
} from '@tablesready/common-ui';
import { angleChanged } from '../../../objects.methods';
import { FrontendModalService } from '@tablesready/frontend-modal';

@Component({
  selector: 'tablesready-floorplan-add-object-form',
  templateUrl: './add-object-form.component.html',
  styleUrls: ['./add-object-form.component.scss'],
})
export class AddObjectFormComponent implements OnChanges, OnDestroy {
  @Input() rotation?: number;
  @Input() width = 0;
  @Input() height = 0;
  @Input() left = 0;
  @Input() top = 0;
  @Input() shape = Shapes.rectangular;
  @Input() floorPlanId = '';
  @Input() objectId?: string;
  @Input() name = '';
  @Input() isEditMode = false;
  @Input() onCreate?: (name: string, attributes: ObjectAttributes) => void;
  @Input() onDelete?: (id: string, floorPlanId: string) => void;
  @Input() onSave?: (id: string,
    floorPlanId: string,
    objectName: string,
    attributes: ObjectAttributes) => void;

  @Output() formTouched = new EventEmitter<boolean>();
  @Output() formClosed = new EventEmitter<boolean>();
  @Output() nameChange = new EventEmitter<string>();
  @Output() tableRemoved = new EventEmitter<string>();
  @Output() rotationChange = new EventEmitter<number>();

  form: FormGroup;
  private lastObjectId = '';
  private readonly updateObjectTransform$ = new Subject<boolean>();
  private readonly subscriptions = new Subscription();

  constructor(
    private readonly validationService: ValidationService,
    private readonly formBuilder: FormBuilder,
    private readonly modalController: FrontendModalService,
  ) {}

  init(): void {
    this.form = this.formBuilder.group({
      name: [''],
      shape: [this.shape],
      rotation: [0, [Validators.required, Validators.min(0), Validators.max(360)]],
    });

    const touchedSub = this.form.valueChanges
      .subscribe(() => {
        this.formTouched.emit(this.form.touched);
      });
    this.subscriptions.add(touchedSub);
  }

  delete(): void {
    this.modalController.create<ConfirmationModalComponent, ConfirmationModalResult<boolean>>(ConfirmationModalComponent, {
      options: {
        content: 'Are you sure you want to delete the object',
        title: 'Confirm object deletion',
        closeBtnText: 'Cancel',
      },
    })
      .pipe(
        take(1),
        filter(res => res?.response),
        tap(() => {
          if (!this.onDelete) {
            return;
          }

          this.onDelete(this.objectId, this.floorPlanId);
          this.formClosed.emit(false);
        }),
      )
      .subscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.objectId && this.objectId !== this.lastObjectId) {
      this.init();
      this.lastObjectId = this.objectId;
    }

    const formPropsToCheck = ['name', 'rotation', 'shape'];
    formPropsToCheck.forEach((prop) => {
      if (changes[prop] && changes[prop].currentValue !== changes[prop].previousValue) {
        this.form?.get(prop)?.setValue(changes[prop].currentValue, { emitEvent: false });

        if (changes[prop].previousValue !== undefined) {
          this.formTouched.emit(true);
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();

    this.formClosed.complete();
    this.tableRemoved.complete();
    this.rotationChange.complete();
  }

  saveObject(): void {
    this.validationService.validateForm(this.form);
    if (!this.onCreate || !this.onSave || !this.form.valid) {
      return;
    }

    const name = this.form.get('name')?.value;
    const attributes: ObjectAttributes = {
      shape: this.form.get('shape')?.value || Shapes.rectangular,
      size: TableSizes.custom,
      width: this.width || 50,
      height: this.height || 50,
      top: this.top || 1,
      left: this.left || 1,
      rotation: this.form.get('rotation')?.value || 0,
    };

    this.lastObjectId = undefined;
    if (this.isEditMode) {
      this.onSave(this.objectId, this.floorPlanId, name, attributes);
      this.formClosed.emit(false);
    } else {
      this.onCreate(name, attributes);
      this.formClosed.emit(false);
    }
  }

  resetAngle(): void {
    this.form.get('rotation').setValue(0);
    this.rotationChange.emit(0);
    this.formTouched.emit(true);
  }

  cancel(): void {
    if (this.isEditMode && !this.form.touched) {
      this.formClosed.emit(true);

      return;
    }

    this.modalController.create<ConfirmationModalComponent, ConfirmationModalResult<boolean>>(ConfirmationModalComponent, {
      options: {
        content: 'Leave the form? Changes that you made will not be saved.',
        title: 'Cancel object changes',
        closeBtnText: 'Cancel',
      },
    })
      .pipe(
        take(1),
        filter((res) => res?.response),
        tap(() => {
          if (!this.isEditMode) {
            this.tableRemoved.emit(this.objectId);
          }
          this.formClosed.emit(true);
        }),
      )
      .subscribe();
  }

  protected readonly angleChanged = angleChanged;
}
